import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, ModalBody } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
	Form,
	FormGroup,
	Row,
	Col,
	Input,
	FormFeedback,
	Label,
} from "reactstrap";
import Select from "react-select";
import userService from "services/UserService";
import organizationalStructureService from "services/OrganizationalStructureService";
import Swal from "sweetalert2";
import MigrationModal from "./MigrationModal";
import {
	CheckSquare,
	FloppyDisk,
	SignOut,
	Square,
	Copy,
} from "@phosphor-icons/react";
import { Checkbox } from "@mui/material";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const OrganizationalStructure = ({
	show,
	onCloseClick,
	data,
	updateData,
	onDuplicate,
	onDuplicatedDepartmentSaved,
}) => {
	const id = data?.id;

	const [usuarios, setUsuarios] = useState([]);
	const [organizationalEstructures, setOrganizationaEstructures] = useState(
		[],
	);
	const [organizationalEstructuresFull, setOrganizationalEstructuresFull] =
		useState([]);
	const [showMigrationModal, setShowMigrationModal] = useState(false);
	const [linkedItems, setLinkedItems] = useState(null);
	const [migrationTargetId, setMigrationTargetId] = useState(null);
	const [pendingValues, setPendingValues] = useState(null);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			name: data?.name || "",
			parent_id: data?.parent_id || "",
			responsible_name: data?.responsible_name || "",
			active: data?.active === "0" ? 0 : 1,
			color_name: data?.color_name || "#2a3042",
			color_responsible: data?.color_responsible || "#ffffff",
			font_family: data?.font_family || "Padrão do Sistema",
			is_support_area: data?.is_support_area || false,
		},

		validationSchema: Yup.object({
			name: Yup.string().required("Por favor, informe um nome!"),
			responsible_name: Yup.string().required(
				"Por favor, informe um responsável!",
			),
		}),

		onSubmit: async (values, { resetForm, setSubmitting }) => {
			const errors = await validation.validateForm(values);
			if (Object.keys(errors).length === 0) {
				// Validação: evitar que um departamento seja seu próprio pai ou escolha um descendente
				if (id && values.parent_id) {
					const getAllDescendants = (deptId) => {
						const descendants = new Set();
						const findChildren = (parentId) => {
							organizationalEstructuresFull
								.filter((d) => Number(d.parent_id) === Number(parentId))
								.forEach((child) => {
									descendants.add(Number(child.id));
									findChildren(child.id);
								});
						};
						findChildren(deptId);
						return descendants;
					};
					const descendants = getAllDescendants(id);
					if (Number(values.parent_id) === Number(id) || descendants.has(Number(values.parent_id))) {
						SendaCustomSwal.fire({
							iconHtml: '<i class="error-icon"></i>',
							title: "Erro de Validação",
							text: "Um departamento não pode ser seu próprio pai ou escolher um de seus descendentes como pai.",
							customClass: {
								confirmButton: "custom-confirm-button",
							},
						});
						setSubmitting(false);
						return;
					}
				}

				if (!values.parent_id) {
					try {
						const response =
							await organizationalStructureService.get();
						const rootEntities = response.filter(
							(item) => item.parent_id === null && item.id !== id,
						);

						if (rootEntities.length > 0) {
							SendaCustomSwal.fire({
								iconHtml: '<i class="error-icon"></i>',
								title: "Oops...",
								text: "Já existe uma entidade sem área acima.",
								customClass: {
									confirmButton: "custom-confirm-button",
								},
							});
							setSubmitting(false);
							return;
						}
					} catch (error) {
						SendaCustomSwal.fire({
							iconHtml: '<i class="error-icon"></i>',
							title: "Oops...",
							text: "Erro ao verificar entidades sem área acima.",
							customClass: {
								confirmButton: "custom-confirm-button",
							},
						});
						setSubmitting(false);
						return;
					}
				}

				// comentando essa parte porque com a atividade #5065 o usuário pode migrar os intens na aba migrações
				// if (values.active == 0) {
				// 	// O usuário está desativando a área
				// 	try {
				// 		const response =
				// 			await organizationalStructureService.getLinkedItems(
				// 				id,
				// 			);
				// 		if (
				// 			response.goals.length > 0 ||
				// 			response.actions_plans.length > 0 ||
				// 			response.ata_referrals.length > 0 ||
				// 			response.ata_definitions.length > 0
				// 		) {
				// 			// Há itens vinculados, exibir modal de migração
				// 			setLinkedItems(response);
				// 			setShowMigrationModal(true);
				// 			setPendingValues(values);
				// 			setSubmitting(false);
				// 			return; // Sair do onSubmit, prosseguiremos após a migração
				// 		}
				// 	} catch (error) {
				// 		SendaCustomSwal.fire({
				// 			iconHtml: '<i class="error-icon"></i>',
				// 			title: "Oops...",
				// 			text: "Erro ao verificar itens vinculados.",
				// 			customClass: {
				// 				confirmButton: "custom-confirm-button",
				// 			},
				// 		});
				// 		setSubmitting(false);
				// 		return;
				// 	}
				// }

				try {
					let val = values;
					let newDepartmentId = null;

					if (values.parent_id === "") val.parent_id = null;

					// Adicionar flag isDuplicated se este departamento foi duplicado
					if (data?.isDuplicated) {
						val.isDuplicated = true;
						val.originalDepartmentId = data.originalId;
					}

					if (id) {
						await organizationalStructureService.put(id, val);
						newDepartmentId = id;

						 // Verificar se foi uma desativação e se há itens vinculados
						 if (data?.active === "1" && values.active === 0) {
							try {
								const linkedItemsResponse = await organizationalStructureService.getLinkedItems(id);
								
								// Verificar se há algum item vinculado
								const hasLinkedItems = linkedItemsResponse.goals?.length > 0 || linkedItemsResponse.actions_plans?.length > 0 || linkedItemsResponse.ata_referrals?.length > 0;
								
								if (hasLinkedItems) {
									// Construir mensagem personalizada
									const linkedItemsTypes = [];
									if (linkedItemsResponse.goals?.length > 0) linkedItemsTypes.push("indicadores");
									if (linkedItemsResponse.actions_plans?.length > 0) linkedItemsTypes.push("planos de ação");
									if (linkedItemsResponse.ata_referrals?.length > 0) linkedItemsTypes.push("encaminhamentos");
									
									const formatListWithAnd = (items) => {
										if (items.length === 1) return items[0];
										if (items.length === 2) return `${items[0]} e ${items[1]}`;
										if (items.length > 2) {
											const lastItem = items.pop(); // Remove o último item
											return `${items.join(', ')} e ${lastItem}`;
										}
										return '';
									};
									
									const formattedTypes = formatListWithAnd(linkedItemsTypes);
									const message = `Este departamento possui itens vinculados (${formattedTypes}). A migração deve ser realizada na tela Migrações.`;
									
									await SendaCustomSwal.fire({
										iconHtml: '<i class="success-icon"></i>',
										title: "Departamento desativado",
										text: message,
										customClass: {
											confirmButton: "custom-confirm-button",
										},
									});
								}
							} catch (error) {
								console.error("Erro ao verificar itens vinculados:", error);
							}
						}
					} else {
						const response =
							await organizationalStructureService.post(val);

						// A API agora retorna: {message: "Organização criada com sucesso", id: organizationalStructure.id}
						newDepartmentId = response.id;
					}

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Formulário enviado com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					}).then(() => {
						// Chama a função se foi passada (para departamentos duplicados)
						if (onDuplicatedDepartmentSaved) {
							onCloseClick();
							resetForm();
							onDuplicatedDepartmentSaved(newDepartmentId);
							setSubmitting(false);
							updateData();
							return;
						}

						// Fluxo normal (não duplicado)
						setSubmitting(false);
						onCloseClick();
						updateData();
						resetForm();
					});
				} catch (error) {
					const message = error?.response?.data?.message
						? error?.response?.data?.message
						: "Algo deu errado!";
					SendaCustomSwal.fire({
						iconHtml: '<i class="error-icon"></i>',
						title: "Oops...",
						text: message,
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
					setSubmitting(false);
				}
			} else {
				setSubmitting(false);
			}
		},
	});

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const onConfirmMigration = async () => {
		if (!migrationTargetId) {
			SendaCustomSwal.fire({
				iconHtml: '<i class="warning-icon"></i>',
				title: "Aviso",
				text: "Por favor, selecione uma área para migrar os itens.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			return;
		}
		try {
			await organizationalStructureService.migrateLinkedItems(
				id,
				migrationTargetId,
			);
			let val = pendingValues;
			if (val.parent_id === "") val.parent_id = null;
			if (id) {
				await organizationalStructureService.put(id, val);
			} else {
				await organizationalStructureService.post(val);
			}
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Itens migrados e área desativada com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			const message = error?.response?.data?.message
				? error?.response?.data?.message
				: "Algo deu errado!";
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: message,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		setShowMigrationModal(false);
		onCloseClick();
		updateData();
		validation.resetForm();
	};

	const handleDuplicate = () => {
		// Preparar os dados para duplicação
		const duplicatedData = {
			name: `${validation.values.name} - Cópia`,
			parent_id: validation.values.parent_id,
			responsible_name: validation.values.responsible_name,
			active: 1, // Sempre ativo por padrão
			color_name: validation.values.color_name,
			color_responsible: validation.values.color_responsible,
			font_family: validation.values.font_family,
			is_support_area: validation.values.is_support_area,
			isDuplicated: true, // Marca que este é um departamento duplicado
			originalId: id, // ID do departamento original
		};

		// Fechar o modal atual
		onCloseClick();

		// Abrir novo modal com os dados duplicados
		if (onDuplicate) {
			onDuplicate(duplicatedData);
		}
	};

	async function getUsuarios() {
		try {
			const response = await userService.get();
			const funcoes = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	}

	async function getOrganizationalEstructure() {
		try {
			const response = await organizationalStructureService.get();

			setOrganizationalEstructuresFull(response);

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));

			setOrganizationaEstructures(options);
		} catch (error) {
			console.log(error);
		}
	}

	useEffect(() => {
		getUsuarios();
		getOrganizationalEstructure();
	}, [show]);

	// Filtrar opções do select: remover próprio departamento e descendentes
	const getFilteredOptions = () => {
		if (!id) return organizationalEstructures;
		const getAllDescendants = (deptId) => {
			const descendants = new Set();
			const findChildren = (parentId) => {
				organizationalEstructuresFull
					.filter((d) => d.parent_id !== null && Number(d.parent_id) === Number(parentId))
					.forEach((child) => {
						descendants.add(Number(child.id));
						findChildren(child.id);
					});
			};
			findChildren(deptId);
			return descendants;
		};
		const descendants = getAllDescendants(id);
		return organizationalEstructures.filter(
			(opt) => Number(opt.value) !== Number(id) && !descendants.has(Number(opt.value))
		);
	};

	return (
		<Col md="12">
			<Form
				className="needs-validation"
				onSubmit={(e) => {
					e.preventDefault();
					validation.handleSubmit();
					return false;
				}}
			>
				<Row>
					<Col md="10">
						<FormGroup className="mb-3">
							<Label htmlFor="validationCustom01">
								Departamento Pai
							</Label>
							<Select
								styles={customStyles}
								placeholder="Selecione o Departamento Pai"
								isClearable={true}
								onChange={(selectedOption) => {
									const selectedValue = selectedOption
										? selectedOption.value
										: null;
									validation.setFieldValue(
										"parent_id",
										selectedValue,
									);
								}}
								value={
									getFilteredOptions()?.find(
										(option) =>
											option.value ===
											validation.values.parent_id,
									) || null
								}
								options={getFilteredOptions()}
							/>
						</FormGroup>
					</Col>

					<Col md="2">
						<FormGroup className="mb-3">
							<Label htmlFor="fontFamily">Fonte da Caixa</Label>
							<Select
								name="font_family"
								value={
									[
										{
											value: "Padrão do Sistema",
											label: "Padrão do Sistema",
										},
										{ value: "Arial", label: "Arial" },
										{
											value: "Times New Roman",
											label: "Times New Roman",
										},
										{
											value: "Algerian",
											label: "Algerian",
										},
										{
											value: "Britannic",
											label: "Britannic",
										},
										{
											value: "Lucida Calligraphy",
											label: "Lucida Calligraphy",
										},
										{ value: "Tahoma", label: "Tahoma" },
									].find(
										(option) =>
											option.value ===
											validation.values.font_family,
									) || {
										value: "Padrão do Sistema",
										label: "Padrão do Sistema",
									}
								}
								onChange={(selectedOption) => {
									validation.setFieldValue(
										"font_family",
										selectedOption.value ===
											"Padrão do Sistema"
											? null
											: selectedOption.value,
									);
								}}
								options={[
									{
										value: "Padrão do Sistema",
										label: "Padrão do Sistema",
									},
									{ value: "Arial", label: "Arial" },
									{
										value: "Times New Roman",
										label: "Times New Roman",
									},
									{ value: "Algerian", label: "Algerian" },
									{ value: "Britannic", label: "Britannic" },
									{
										value: "Lucida Calligraphy",
										label: "Lucida Calligraphy",
									},
									{ value: "Tahoma", label: "Tahoma" },
								]}
								styles={customStyles}
							/>
						</FormGroup>
					</Col>

					<Col md="10">
						<FormGroup className="mb-3">
							<Label htmlFor="definition">
								Nome <span className="text-danger">*</span>
							</Label>
							<Input
								name="name"
								type="text"
								placeholder="Insira o nome."
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.name || ""}
								invalid={
									!!validation.touched.name &&
									!!validation.errors.name
								}
							/>
							<FormFeedback type="invalid">
								{validation.errors.name}
							</FormFeedback>
						</FormGroup>
					</Col>

					<Col md="2">
						<FormGroup className="mb-3">
							<Label htmlFor="colorName">Cor do Nome</Label>
							<Input
								name="color_name"
								type="color"
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={
									validation.values.color_name || "#2a3042"
								}
								style={{
									width: "100%",
									height: "38px",
									cursor: validation.values.is_support_area
										? "not-allowed"
										: "pointer",
								}}
								disabled={validation.values.is_support_area}
							/>
						</FormGroup>
					</Col>

					<Col md="10">
						<FormGroup className="mb-3">
							<Label htmlFor="validationCustom01">
								Responsável{" "}
								<span className="text-danger">*</span>
							</Label>
							<Input
								name="responsible_name"
								type="text"
								placeholder="Insira o responsável."
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={validation.values.responsible_name || ""}
								invalid={
									!!validation.touched.responsible_name &&
									!!validation.errors.responsible_name
								}
							/>
							<FormFeedback type="invalid">
								{validation.errors.responsible_name}
							</FormFeedback>
						</FormGroup>
					</Col>

					<Col md="2">
						<FormGroup className="mb-3">
							<Label htmlFor="colorResponsible">
								Cor do Responsável
							</Label>
							<Input
								name="color_responsible"
								type="color"
								onChange={validation.handleChange}
								onBlur={validation.handleBlur}
								value={
									validation.values.color_responsible ||
									"#ffffff"
								}
								style={{
									width: "100%",
									height: "38px",
									cursor: "pointer",
								}}
							/>
						</FormGroup>
					</Col>

					<Col md="12">
						<FormGroup className="mb-3">
							<div className="d-flex gap-4">
								<div>
									<Checkbox
										className="form-check-input"
										id="active"
										name="active"
										checked={validation.values.active}
										onChange={(event) => {
											const value = event.target.checked
												? 1
												: 0;
											validation.setFieldValue(
												"active",
												value,
											);
										}}
										style={{
											background: "none",
											border: "none",
											padding: 0,
											cursor: "pointer",
											color: "black",
										}}
										icon={<Square size={16} />}
										checkedIcon={<CheckSquare size={16} />}
									/>
									<label
										className="form-check-label"
										htmlFor="active"
										style={{ marginLeft: 5 }}
									>
										Ativo?
									</label>
								</div>
								<div>
									<Checkbox
										className="form-check-input"
										id="is_support_area"
										name="is_support_area"
										checked={
											validation.values.is_support_area
										}
										onChange={(event) => {
											const value = event.target.checked;
											validation.setFieldValue(
												"is_support_area",
												value,
											);
											if (value) {
												validation.setFieldValue(
													"color_name",
													"#c18f00",
												);
											}
										}}
										style={{
											background: "none",
											border: "none",
											padding: 0,
											cursor: "pointer",
											color: "black",
										}}
										icon={<Square size={16} />}
										checkedIcon={<CheckSquare size={16} />}
									/>
									<label
										className="form-check-label"
										htmlFor="is_support_area"
										style={{ marginLeft: 5 }}
									>
										Staff
									</label>
								</div>
							</div>
							{validation.values.active === 0 && (
								<div className="mt-2 text-warning">
									Desativar esta área irá ocultar todos os
									filhos vinculados. Caso houver.
								</div>
							)}
						</FormGroup>
					</Col>
				</Row>

				<Row className="py-1">
					<div
						className={`hstack gap-2 ${
							id
								? "justify-content-between"
								: "justify-content-end"
						} mb-0`}
					>
						{id && (
							<Button
								type="button"
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
								}}
								onClick={handleDuplicate}
							>
								<Copy size={16} /> Duplicar
							</Button>
						)}
						<div className="hstack gap-2">
							<Button
								type="submit"
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									marginLeft: 10,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
								}}
								disabled={validation.isSubmitting}
							>
								<FloppyDisk size={16} /> Salvar
							</Button>
							<Button
								type="button"
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
								}}
								onClick={() => {
									onCloseClick();
								}}
							>
								<SignOut size={16} /> Fechar
							</Button>
						</div>
					</div>
				</Row>
			</Form>

			{/* Renderize o MigrationModal aqui */}
			<MigrationModal
				isOpen={showMigrationModal}
				toggle={() => setShowMigrationModal(false)}
				linkedItems={linkedItems}
				organizationalEstructures={organizationalEstructures}
				currentStructureId={id}
				customStyles={customStyles}
				onConfirmMigration={onConfirmMigration}
				setMigrationTargetId={setMigrationTargetId}
			/>
		</Col>
	);
};

OrganizationalStructure.propTypes = {
	show: PropTypes.any,
	onCloseClick: PropTypes.func,
	data: PropTypes.any,
	updateData: PropTypes.func,
	onDuplicate: PropTypes.func,
	onDuplicatedDepartmentSaved: PropTypes.func,
};

export default OrganizationalStructure;
